import { of as observableOf } from 'rxjs';

import { switchMap, map, catchError, withLatestFrom, filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as actions from '../actions/index';
import { MemberBadgesService } from '@careapp-group/ng-sdk';
import { getAuthenticationState } from '../auth/auth.selectors';
import { Store } from '@ngrx/store';
import { ADMIN_ROLE, CAREAPP_MANAGER } from '@careapp-group/core';

@Injectable()
export class MemberBadgeEffects {
  getMemberBadges = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemberBadgesActions.RequestRetrieveAllAction),
      withLatestFrom(this.store.select(getAuthenticationState)),
      switchMap(([action, auth]) => {
        const isAdmin = !!(
          auth.roles &&
          auth.roles.find((value) => value === ADMIN_ROLE || value === CAREAPP_MANAGER)
        );
        return this.memberBadgeService.listBadges({ includeInactive: isAdmin }).pipe(
          map((res) =>
            actions.MemberBadgesActions.ResponseSuccessRetrieveAllAction({ payload: res })
          ),
          catchError((error) =>
            observableOf(actions.MemberBadgesActions.ResponseFailRetrieveAllAction({ error }))
          )
        );
      })
    )
  );

  // Admin only
  removeMemberBadge = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemberBadgesActions.RequestDeleteByIdAction),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(
        ([action, auth]) =>
          !!(
            auth.roles &&
            auth.roles.find((value) => value === ADMIN_ROLE || value === CAREAPP_MANAGER)
          )
      ),
      switchMap(([action, auth]) => {
        return this.memberBadgeService.deleteMemberBadge({ id: action.id }).pipe(
          map((_) =>
            actions.MemberBadgesActions.ResponseSuccessDeleteByIdAction({
              id: action.id,
            })
          ),
          catchError((error) =>
            observableOf(
              actions.MemberBadgesActions.ResponseFailDeleteByIdAction({ id: action.id, error })
            )
          )
        );
      })
    )
  );

  // Admin only
  addMemberBadge = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemberBadgesActions.RequestCreateAction),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(
        ([action, auth]) =>
          !!(
            auth.roles &&
            auth.roles.find((value) => value === ADMIN_ROLE || value === CAREAPP_MANAGER)
          )
      ),
      switchMap(([action, auth]) => {
        return this.memberBadgeService
          .createMemberBadge({
            memberBadgeCreateRequestDto: action.payload,
          })
          .pipe(
            map((res) => actions.MemberBadgesActions.ResponseSuccessCreateAction({ payload: res })),
            catchError((error) =>
              observableOf(actions.MemberBadgesActions.ResponseFailCreateAction({ error }))
            )
          );
      })
    )
  );

  // Admin only
  updateMemberBadge = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemberBadgesActions.RequestUpdateAction),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(
        ([action, auth]) =>
          !!(
            auth.roles &&
            auth.roles.find((value) => value === ADMIN_ROLE || value === CAREAPP_MANAGER)
          )
      ),
      switchMap(([action, auth]) => {
        return this.memberBadgeService
          .updateMemberBadge({
            id: action.id,
            memberBadgeUpdateRequestDto: action.data,
          })
          .pipe(
            map((res) => actions.MemberBadgesActions.ResponseSuccessUpdateAction({ payload: res })),
            catchError((error) =>
              observableOf(
                actions.MemberBadgesActions.ResponseFailUpdateAction({ error, id: action.id })
              )
            )
          );
      })
    )
  );

  addBadgeToPermission = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemberBadgesActions.RequestAddBadgeToPermission),
      switchMap((action) => {
        return this.memberBadgeService
          .addBadgeToPermission({
            id: action.badgeId,
            permissionId: action.permissionId,
          })
          .pipe(
            map((payload) =>
              actions.MemberBadgesActions.ResponseSuccessAddBadgeToPermission({ payload })
            ),
            catchError((error) =>
              observableOf(
                actions.MemberBadgesActions.ResponseFailAddBadgeToPermission({
                  error,
                  badgeId: action.badgeId,
                  permissionId: action.permissionId,
                })
              )
            )
          );
      })
    )
  );
  removeBadgeFromPermission = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.MemberBadgesActions.RequestRemoveBadgeFromPermission),
      switchMap((action) => {
        return this.memberBadgeService
          .removeBadgeFromPermission({
            id: action.badgeId,
            permissionId: action.permissionId,
          })
          .pipe(
            map((payload) =>
              actions.MemberBadgesActions.ResponseSuccessRemoveBadgeFromPermission({ payload })
            ),
            catchError((error) =>
              observableOf(
                actions.MemberBadgesActions.ResponseFailRemoveBadgeFromPermission({
                  error,
                  badgeId: action.badgeId,
                  permissionId: action.permissionId,
                })
              )
            )
          );
      })
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<actions.CAAction>,
    private memberBadgeService: MemberBadgesService
  ) {}
}
